'use client'

import { useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { useIsMobile } from '@/hooks/use-mobile'
import Navbar from '@/components/home/Navbar'
import Footer from '@/components/home/Footer'
import NewsletterSignup from '@/app/blog/NewsletterSignup'
import { useButtonTracking } from '@/lib/useButtonTracking'

const REPO_URL = 'https://github.com/AI-Builder-Club/skills'
const VIDEO_URL = 'https://youtu.be/W6x-hb44C0c'
const INSTALL_COMMANDS =
  '/plugin marketplace add AI-Builder-Club/skills\n/plugin install skills@ai-builder-club'

const skillUrl = (slug: string) => `${REPO_URL}/blob/main/skills/${slug}/SKILL.md`

const skillGroups: {
  title: string
  tagline: string
  skills: { slug: string; blurb: string }[]
}[] = [
  {
    title: 'Codebase harness',
    tagline: 'make a repo agent-ready',
    skills: [
      {
        slug: 'setup-codebase-harness',
        blurb: 'Onboarding a repo to agent-driven dev. The master skill that orchestrates the four below.',
      },
      {
        slug: 'dev-local-setup',
        blurb: 'A one-command local dev stack. scripts/dev-local.sh up, and the agent has an app to test against.',
      },
      {
        slug: 'e2e-setup',
        blurb: 'The repo has no real e2e? Add a per-PR test gate agents must pass before shipping.',
      },
      {
        slug: 'crabbox-setup',
        blurb: "Loops shipping code in parallel each get an isolated cloud stack. One laptop can't run N.",
      },
      {
        slug: 'verifier-setup',
        blurb: 'Scaffolds a repo-specific /verify skill: a fresh sub-agent drives the app and attaches screenshot proof to the PR.',
      },
    ],
  },
  {
    title: 'Loops',
    tagline: 'the shared knowledge base',
    skills: [
      {
        slug: 'new-loop',
        blurb: 'Spin up a new loop or workstream the agent owns. Bootstraps the knowledge base on first run.',
      },
    ],
  },
  {
    title: 'Utilities',
    tagline: 'standalone, use anywhere',
    skills: [
      {
        slug: 'agent-context-audit',
        blurb: "Audit your CLAUDE.md, docs, and skills against Anthropic's context-engineering guidance. Finds conflicts, stale facts, and overconstraint.",
      },
      {
        slug: 'open-agent-teams',
        blurb: 'Delegate work to any CLI agent (claude, codex, aider) in a detached tmux session with a race-safe done signal.',
      },
      {
        slug: 'seo-growth',
        blurb: 'Where to point SEO effort: a cold-start playbook and a with-data playbook, distilled from two sites we run in production.',
      },
      {
        slug: 'visual-flow-gif',
        blurb: 'Turn an article, workflow, or architecture into a static PNG plus an animated GIF flow diagram.',
      },
    ],
  },
]

const ingredients = [
  {
    num: '01',
    title: 'Triggers',
    blurb: 'A cron, a webhook, an incident, or another agent wakes the loop at the right time.',
    shipped: false,
    tag: 'You wire',
  },
  {
    num: '02',
    title: 'Shared memory',
    blurb: 'The file and logging structure every loop reads and writes. One brain, many loops.',
    shipped: true,
    tag: 'Ships here',
  },
  {
    num: '03',
    title: 'Tools & connectors',
    blurb: 'Your skills and MCPs, so the agent can do real work in your systems.',
    shipped: false,
    tag: 'You bring',
  },
  {
    num: '04',
    title: 'Codebase harness',
    blurb: 'So the agent can run, test, and verify its own work before it ships.',
    shipped: true,
    tag: 'Ships here',
  },
]

export default function SkillsContent({ stars }: { stars: number }) {
  const isMobile = useIsMobile()
  const { trackButtonClick } = useButtonTracking()
  const [copied, setCopied] = useState(false)

  const trackRepoClick = (placement: string, extra?: Record<string, string>) => {
    trackButtonClick('skills_repo_cta_click', {
      section: 'skills_page',
      placement,
      destination: 'github_skills_repo',
      conversion: true,
      ...extra,
    })
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(INSTALL_COMMANDS).then(() => {
      setCopied(true)
      trackButtonClick('skills_install_copied', { section: 'skills_page' })
      setTimeout(() => setCopied(false), 1600)
    })
  }

  const starLabel = stars.toLocaleString('en-US')

  return (
    <main className="bg-black text-white">
      <Navbar isMobile={isMobile} isHomePage={false} />

      {/* Hero */}
      <section className="flex flex-col items-center pt-28 sm:pt-44 pb-16 sm:pb-20 px-4 text-center">
        <div className="flex justify-center mb-6">
          <span className="inline-flex items-center gap-2 border border-white/15 bg-white/5 text-white/80 rounded-full px-4 py-2 text-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
            Open source · 10 skills · MIT
          </span>
        </div>

        <h1 className={`${isMobile ? 'text-4xl' : 'text-6xl'} font-bold mb-5 tracking-tight`}>
          The skills my team runs
          <br />
          in production.
        </h1>

        <p className={`${isMobile ? 'text-lg' : 'text-2xl'} text-white/70 max-w-2xl mx-auto mb-10`}>
          10 Claude Code skills: onboard any repo to agent-driven dev, give every agent its own
          stack, make the work verifiable, build loops that keep going.
        </p>

        {/* Install block */}
        <div className="w-full max-w-2xl mx-auto text-left relative rounded-xl border border-gray-700 bg-[#0d1117] shadow-lg px-6 py-5 mb-3">
          <button
            onClick={handleCopy}
            className="absolute top-3 right-3 border border-gray-700 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white rounded-md px-3 py-1 text-xs transition-colors"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
          <pre className="font-mono text-sm leading-8 text-green-400 overflow-x-auto">
            {INSTALL_COMMANDS}
          </pre>
        </div>
        <p className="text-sm text-white/50 mb-10">One plugin, all 10 skills. Works in any repo.</p>

        <div className="flex flex-wrap justify-center gap-3">
          <a
            href={REPO_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackRepoClick('hero')}
            className="inline-flex items-center bg-white text-black px-8 py-3 rounded-full font-medium hover:bg-white/90 transition-colors group"
          >
            View on GitHub
            <span className="ml-2 text-sm text-black/60">★ {starLabel}</span>
            <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
          </a>
          <a
            href={VIDEO_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackButtonClick('skills_video_click', { section: 'skills_page', placement: 'hero' })
            }
            className="inline-flex items-center border border-white/15 text-white/80 hover:text-white hover:border-white/35 px-8 py-3 rounded-full font-medium transition-colors"
          >
            Watch the walkthrough
          </a>
        </div>

        {/* Receipts */}
        <div className="mt-14 flex flex-col sm:flex-row justify-center items-center gap-6 sm:gap-0 sm:divide-x sm:divide-white/10">
          <div className="px-8 text-center">
            <p className="text-2xl font-bold text-white">30+ loops</p>
            <p className="text-sm text-white/50 max-w-[220px] mt-1">
              running our 3 brands on these skills
            </p>
          </div>
          <div className="px-8 text-center">
            <p className="text-2xl font-bold text-white">#1 clicked page</p>
            <p className="text-sm text-white/50 max-w-[220px] mt-1">
              on our site 7 days after one loop shipped it (seo-growth)
            </p>
          </div>
          <div className="px-8 text-center">
            <p className="text-2xl font-bold text-white">-4 to 95 karma</p>
            <p className="text-sm text-white/50 max-w-[220px] mt-1">
              on Reddit in 7 days, run end to end by one loop
            </p>
          </div>
        </div>
      </section>

      {/* The concept */}
      <section className="bg-white/[0.02] py-16 sm:py-20 px-4">
        <div className="max-w-3xl mx-auto text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            A loop is an agent that owns a workstream
          </h2>
          <p className="text-lg text-white/60">
            It wakes on a trigger, does the work, and writes what it found into a shared,
            file-based memory. Next run it reads that memory and keeps going. Building one takes
            four ingredients. <strong className="text-white/90">This plugin ships two of them.</strong>
          </p>
        </div>
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ingredients.map((ing) => (
            <div
              key={ing.num}
              className={`rounded-2xl border p-6 bg-zinc-900/50 ${
                ing.shipped ? 'border-green-500/40' : 'border-white/10'
              }`}
            >
              <p className="text-sm text-white/40 font-medium mb-3">{ing.num}</p>
              <h3 className="text-white font-semibold mb-2">{ing.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{ing.blurb}</p>
              <span
                className={`inline-flex items-center gap-1.5 mt-4 text-xs font-medium px-2.5 py-1 rounded-full ${
                  ing.shipped ? 'bg-green-500/10 text-green-400' : 'bg-white/5 text-white/50'
                }`}
              >
                <span
                  className={`w-1 h-1 rounded-full ${ing.shipped ? 'bg-green-500' : 'bg-white/30'}`}
                />
                {ing.tag}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Two entry points */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-3xl mx-auto text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">Two entry points</h2>
          <p className="text-lg text-white/60">
            Everything else in the plugin hangs off these two commands.
          </p>
        </div>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
          <a
            href={skillUrl('setup-codebase-harness')}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackRepoClick('entry', { skill: 'setup-codebase-harness' })}
            className="block rounded-2xl border border-white/10 hover:border-white/25 bg-zinc-900/50 hover:bg-zinc-900/80 p-8 transition-colors group"
          >
            <span className="inline-block font-mono text-sm text-green-400 bg-gray-900 rounded-md px-2.5 py-1 mb-4">
              /setup-codebase-harness
            </span>
            <h3 className="text-xl font-semibold text-white mb-2">Make a repo agent-ready</h3>
            <p className="text-[15px] text-white/60 leading-relaxed">
              Run it in the code repo your agents work in, so they can run, test, and verify their
              own work. It orchestrates the four harness skills below and pulls in only what the
              repo needs.
            </p>
            <span className="inline-flex items-center gap-1.5 mt-5 text-sm font-medium text-white">
              Read the skill
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </span>
          </a>
          <a
            href={skillUrl('new-loop')}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackRepoClick('entry', { skill: 'new-loop' })}
            className="block rounded-2xl border border-white/10 hover:border-white/25 bg-zinc-900/50 hover:bg-zinc-900/80 p-8 transition-colors group"
          >
            <span className="inline-block font-mono text-sm text-green-400 bg-gray-900 rounded-md px-2.5 py-1 mb-4">
              /new-loop
            </span>
            <h3 className="text-xl font-semibold text-white mb-2">Build your shared brain</h3>
            <p className="text-[15px] text-white/60 leading-relaxed">
              Run it where your agent&apos;s memory should live. First run bootstraps the knowledge
              base, then scaffolds the loop and does one real test run. Run it again any time to
              add another loop.
            </p>
            <span className="inline-flex items-center gap-1.5 mt-5 text-sm font-medium text-white">
              Read the skill
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </span>
          </a>
        </div>
      </section>

      {/* The full kit */}
      <section className="bg-white/[0.02] py-16 sm:py-20 px-4">
        <div className="max-w-3xl mx-auto text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            10 skills, grouped by job
          </h2>
          <p className="text-lg text-white/60">
            Every skill is a folder you can read before you run it. No black boxes.
          </p>
        </div>
        <div className="max-w-6xl mx-auto">
          {skillGroups.map((group) => (
            <div key={group.title} className="mb-10 last:mb-0">
              <div className="flex items-baseline gap-2.5 mb-3.5">
                <h3 className="text-white font-semibold">{group.title}</h3>
                <span className="text-sm text-white/40">{group.tagline}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                {group.skills.map((skill) => (
                  <a
                    key={skill.slug}
                    href={skillUrl(skill.slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackRepoClick('grid', { skill: skill.slug })}
                    className="relative block rounded-xl border border-white/10 hover:border-white/25 bg-zinc-900/50 hover:bg-zinc-900/80 p-5 transition-colors group"
                  >
                    <span className="absolute top-4 right-4 text-white/25 group-hover:text-white/70 text-sm transition-colors">
                      ↗
                    </span>
                    <span className="block font-mono text-[13.5px] text-white mb-2">
                      {skill.slug}
                    </span>
                    <p className="text-[13.5px] text-white/55 leading-relaxed">{skill.blurb}</p>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Newsletter capture */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-3xl mx-auto text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            New skills ship to the newsletter first
          </h2>
          <p className="text-lg text-white/60">
            When we add a skill or change how we run our loops, subscribers hear about it before
            anyone else.
          </p>
        </div>
        <div className="max-w-2xl mx-auto">
          <NewsletterSignup source="skills_page" variant="inline" />
        </div>
      </section>

      {/* Video */}
      <section className="pb-16 sm:pb-20 px-4">
        <a
          href={VIDEO_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            trackButtonClick('skills_video_click', { section: 'skills_page', placement: 'video_card' })
          }
          className="grid grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto rounded-2xl border border-white/10 hover:border-white/25 bg-zinc-900/50 overflow-hidden transition-colors group"
        >
          <div className="relative min-h-[220px] bg-[#111]">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="https://i.ytimg.com/vi/W6x-hb44C0c/maxresdefault.jpg"
              alt="The loop engineer video walkthrough"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center transition-transform duration-200 group-hover:scale-105">
                <span className="ml-1 border-l-[14px] border-l-black border-y-[9px] border-y-transparent" />
              </div>
            </div>
          </div>
          <div className="p-8 flex flex-col justify-center">
            <p className="text-sm text-white/50 font-medium mb-2">Watch</p>
            <h3 className="text-xl sm:text-2xl font-bold text-white leading-snug mb-2.5">
              The loop engineer: how to design compounding agent loops
            </h3>
            <p className="text-[15px] text-white/60">
              The full walkthrough of the concept and how my team designs loops that read and write
              one shared brain.
            </p>
          </div>
        </a>
      </section>

      {/* Final CTA */}
      <section className="bg-white/[0.02] py-20 sm:py-24 px-4 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-xl mx-auto mb-4">
          This is the setup my team runs. Take it.
        </h2>
        <p className="text-lg text-white/60 max-w-xl mx-auto mb-10">
          Open source, readable, and built to be pulled apart. Star it, install it, then come tell
          us what your loops shipped.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <a
            href={REPO_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackRepoClick('final')}
            className="inline-flex items-center bg-white text-black px-8 py-3 rounded-full font-medium hover:bg-white/90 transition-colors group"
          >
            View on GitHub
            <span className="ml-2 text-sm text-black/60">★ {starLabel}</span>
            <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
          </a>
          <a
            href="/#pricing"
            onClick={() =>
              trackButtonClick('skills_join_click', { section: 'skills_page', placement: 'final' })
            }
            className="inline-flex items-center border border-white/15 text-white/80 hover:text-white hover:border-white/35 px-8 py-3 rounded-full font-medium transition-colors"
          >
            Join the community
          </a>
        </div>
      </section>

      <Footer isMobile={isMobile} />
    </main>
  )
}
